<template>
  <Teleport to="body">
    <div v-if="show" class="lightbox" role="dialog" aria-modal="true" @click.self="emit('close')">
      <button class="lightbox__close" type="button" @click="emit('close')" aria-label="Close">
        <Icon icon="mdi:close" />
      </button>
      <button
        v-if="images.length > 1"
        class="lightbox__nav lightbox__nav--prev"
        type="button"
        @click="prev"
        aria-label="Previous image"
      >
        <Icon icon="mdi:chevron-left" />
      </button>
      <img :src="images[index]" :alt="`Image ${index + 1} of ${images.length}`" />
      <button
        v-if="images.length > 1"
        class="lightbox__nav lightbox__nav--next"
        type="button"
        @click="next"
        aria-label="Next image"
      >
        <Icon icon="mdi:chevron-right" />
      </button>
      <div v-if="images.length > 1" class="lightbox__count">
        {{ index + 1 }} / {{ images.length }}
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";

const props = defineProps<{
  show: boolean;
  images: string[];
  startIndex?: number;
}>();

const emit = defineEmits<{ close: [] }>();

const index = ref(props.startIndex || 0);

watch(
  () => [props.show, props.startIndex],
  () => {
    if (props.show) index.value = props.startIndex || 0;
  }
);

const prev = () => {
  index.value = (index.value - 1 + props.images.length) % props.images.length;
};
const next = () => {
  index.value = (index.value + 1) % props.images.length;
};

const onKey = (e: KeyboardEvent) => {
  if (e.key === "Escape") emit("close");
  if (e.key === "ArrowLeft") prev();
  if (e.key === "ArrowRight") next();
};

watch(
  () => props.show,
  (open) => {
    if (open) document.addEventListener("keydown", onKey);
    else document.removeEventListener("keydown", onKey);
  },
  { immediate: true }
);

onBeforeUnmount(() => document.removeEventListener("keydown", onKey));
</script>

<style scoped>
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10200;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lightbox img {
  max-width: 92vw;
  max-height: 88vh;
  border-radius: 4px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}
.lightbox__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lightbox__close:hover {
  background: rgba(255, 255, 255, 0.25);
}
.lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lightbox__nav:hover {
  background: rgba(255, 255, 255, 0.25);
}
.lightbox__nav--prev {
  left: 24px;
}
.lightbox__nav--next {
  right: 24px;
}
.lightbox__count {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.5);
  padding: 4px 10px;
  border-radius: 12px;
}
</style>
